'use client';

// This component must be present in a page's layout for useMfeLoadStore and any
// hook that depends on it (e.g. useAccountModalReady) to function correctly.
// See the JSDoc on useMfeLoadStore for current page coverage and guidance on
// extending it to additional layouts.

import { eventNames, useCoreEvents, type Events } from '@carvana/core-events';
import { useCallback } from 'react';
import {
  COMPOSER_MODULE,
  SOFTPULL_MODULE
} from '~/constants/common/MfeModules';
import {
  ACCOUNT_MODAL_MODULE,
  CHAT_UI_MODULE,
  useMfeLoadStore
} from '~/state/common/MfeLoadState';

export default function MfeEventsListener() {
  const record = useMfeLoadStore(state => state.record);

  const handleMfeEvent = useCallback(
    (event: {
      componentName?: string;
      data?: string;
      eventName?: string;
      moduleName?: string;
    }) => {
      if (
        event.eventName === 'react-component' &&
        event.componentName &&
        event.data &&
        event.moduleName
      ) {
        record({
          componentName: event.componentName,
          data: event.data as 'error' | 'loaded' | 'timeout',
          moduleName: event.moduleName,
          timestamp: Date.now()
        });
      }
    },
    [record]
  );

  // MFER_EVENTS 'loaded' fires when the MFE bundle registers in globalThis — before its React
  // tree renders or initialises. ACCOUNT_MODAL_READY is the header MFE's application-level
  // signal that CoreAccountModal is fully initialised and safe to receive openAccountModal calls.
  // We record it as 'ready' so CTA components can gate on the correct semantic signal.
  // Pre-REO-8289: once the framework ships markReady/teardown props, MFEs will emit
  // MFER_EVENTS 'ready' directly and this listener can be removed.
  const handleAccountModalReady = useCallback(() => {
    record({
      componentName: 'default',
      data: 'ready',
      moduleName: ACCOUNT_MODAL_MODULE,
      timestamp: Date.now()
    });
  }, [record]);

  // The Header MFE persists across SRP/VDP SPA navigations, so CHAT_UI_READY only fires once
  // per page session. withHistory replays it to consumers that mount later (e.g. VDP's
  // SebastianContextProvider on a subsequent SPA navigation).
  const handleChatUiReady = useCallback(() => {
    record({
      componentName: 'default',
      data: 'ready',
      moduleName: CHAT_UI_MODULE,
      timestamp: Date.now()
    });
  }, [record]);

  // Recorded here, not in useToggleModal, so readiness survives page remounts.
  const handleSoftPullModalReady = useCallback(() => {
    record({
      componentName: 'default',
      data: 'ready',
      moduleName: SOFTPULL_MODULE,
      timestamp: Date.now()
    });
  }, [record]);

  // The Composer MFE lives in this same shared layout and persists across SRP/VDP SPA
  // navigations — ComposerContextProvider mounts once for the whole session and never
  // remounts on nav, so COMPOSER_READY only fires once per page session. withHistory
  // replays it to any consumer that subscribes after that single ready event already fired.
  const handleComposerReady = useCallback(
    (payload: Events[typeof eventNames.composer.mfe.ready]) => {
      if (payload.source !== 'composer') return;

      record({
        componentName: 'default',
        data: 'ready',
        moduleName: COMPOSER_MODULE,
        timestamp: Date.now()
      });
    },
    [record]
  );

  const handleComposerUnready = useCallback(
    (payload: Events[typeof eventNames.composer.mfe.unready]) => {
      if (payload.source !== 'composer') return;

      record({
        componentName: 'default',
        data: 'teardown',
        moduleName: COMPOSER_MODULE,
        timestamp: Date.now()
      });
    },
    [record]
  );

  useCoreEvents(eventNames.mfer.events, handleMfeEvent);
  useCoreEvents(eventNames.accountModal.ready, handleAccountModalReady, {
    withHistory: true
  });
  useCoreEvents(eventNames.chatUi.ready, handleChatUiReady, {
    withHistory: true
  });
  useCoreEvents(eventNames.composer.mfe.ready, handleComposerReady, {
    withHistory: true
  });
  useCoreEvents(eventNames.composer.mfe.unready, handleComposerUnready);
  // No withHistory — avoids replaying a stale ready value from a torn-down instance.
  useCoreEvents(eventNames.softpullModal.ready, handleSoftPullModalReady);

  return null;
}
